import { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { cancelCampaignItem } from 'api/campaigns';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AuctionItem } from 'types/domain';

interface CancelAuctionItemProps {
    campaignId: number;
    item: AuctionItem;
    children?: ReactNode;
}

const CancelAuctionItem = (props: CancelAuctionItemProps) => {
    const { item, campaignId, children } = props;
    const toast = useToast();
    const queryClient = useQueryClient();

    const { mutate: cancelItem } = useMutation({
        mutationFn: () =>
            cancelCampaignItem({
                campaignId: campaignId,
                itemId: item.id,
            }),
        onSuccess: () => {
            toast.success('Auction item cancelled successfully.');
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaignId) });
        },
    });

    return (
        <AlertDialog.Root type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>End this listing early?</AlertDialog.Title>
                        <AlertDialog.Description>
                            By confirming, all bids placed will be canceled and result in the item
                            having no winner.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            className={`disabled:cursor-not-allowed disabled:opacity-60`}
                            disabled={!item.can_cancel}
                            onClick={() => cancelItem()}
                        >
                            End listing
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const CancelAuctionItemTrigger = AlertDialog.Trigger;

const Root = CancelAuctionItem;
const Trigger = CancelAuctionItemTrigger;

export {
    CancelAuctionItem,
    CancelAuctionItemTrigger,
    //
    Root,
    Trigger,
};
export type { CancelAuctionItemProps };
